<template>
  <nav class="main-nav">
    <!-- 桌面端导航 -->
    <ul class="desktop-nav hide-mobile">
      <li
        v-for="item in navItems"
        :key="item.name"
        :class="{ active: $route.name === item.name }"
        @click="switchToPage(item.path)"
      >
        {{ item.label }}
      </li>
    </ul>
    
    <!-- 移动端汉堡菜单按钮 -->
    <button 
      class="mobile-menu-btn show-mobile"
      @click="toggleMobileMenu"
      :aria-expanded="isMobileMenuOpen"
      aria-label="切换菜单"
    >
      <span class="hamburger" :class="{ open: isMobileMenuOpen }">
        <span></span>
        <span></span>
        <span></span>
      </span>
    </button>
    
    <!-- 移动端下拉菜单 -->
    <transition name="slide-down">
      <ul v-if="isMobileMenuOpen" class="mobile-nav">
        <li
          v-for="item in navItems"
          :key="item.name"
          :class="{ active: $route.name === item.name }"
          @click="handleMobileNavClick(item.path)"
        >
          {{ item.label }}
        </li>
      </ul>
    </transition>
  </nav>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";

type NavItemType = {
  name: string;
  label: string;
  path: string;
};

const navItems = ref<NavItemType[]>([
  { name: "Home", label: "首页", path: "/" },
  { name: "AboutUs", label: "关于我们", path: "/about-us" },
  { name: "Doctors", label: "专家团队", path: "/doctors" },
  { name: "Departments", label: "科室介绍", path: "/departments" },
  { name: "News", label: "新闻动态", path: "/news" },
]);

const router = useRouter();
const route = useRoute();
const isMobileMenuOpen = ref(false);

const switchToPage = (path: string) => {
  router.push(path);
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const handleMobileNavClick = (path: string) => {
  switchToPage(path);
  isMobileMenuOpen.value = false;
};

// 路由变化时关闭移动端菜单
watch(() => route.path, () => {
  isMobileMenuOpen.value = false;
});

// 点击外部关闭菜单
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.main-nav')) {
    isMobileMenuOpen.value = false;
  }
};

// 组件挂载时添加事件监听
import { onMounted, onUnmounted } from 'vue';
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.main-nav {
  position: relative;
}

/* 桌面端导航 */
.desktop-nav {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 30px;
}

.desktop-nav li {
  cursor: pointer;
  padding: 10px 0;
  position: relative;
  color: #555;
  font-weight: 500;
  transition: color 0.3s;
}

.desktop-nav li:hover,
.desktop-nav li.active {
  color: #1a5fb4;
}

.desktop-nav li.active::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #1a5fb4;
}

.desktop-nav li a {
  color: inherit;
  text-decoration: none;
}

/* 移动端汉堡菜单按钮 */
.mobile-menu-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.hamburger {
  display: flex;
  flex-direction: column;
  width: 24px;
  height: 18px;
  justify-content: space-between;
  cursor: pointer;
}

.hamburger span {
  display: block;
  width: 100%;
  height: 2px;
  background-color: #333;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.hamburger.open span:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.hamburger.open span:nth-child(2) {
  opacity: 0;
}

.hamburger.open span:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

/* 移动端下拉菜单 */
.mobile-nav {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  min-width: 200px;
  list-style: none;
  padding: 10px 0;
  margin: 10px 0 0 0;
  z-index: 1000;
}

.mobile-nav li {
  padding: 12px 20px;
  color: #333;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.mobile-nav li:last-child {
  border-bottom: none;
}

.mobile-nav li:hover,
.mobile-nav li.active {
  background-color: #f0f7ff;
  color: #1a5fb4;
}

/* 下滑动画 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .desktop-nav {
    display: none;
  }
  
  .mobile-menu-btn {
    display: flex;
  }
}

@media (min-width: 769px) {
  .mobile-menu-btn {
    display: none;
  }
  
  .mobile-nav {
    display: none;
  }
}
</style>
